import { EVENT_SITES } from '@crossbeam/itly';

import axios from 'axios';
import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import useDataSharingTracking from '@/composables/useDataSharingTracking';
import { GREENFIELD_SHARING } from '@/constants/data_shares';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores';
import urls from '@/urls';

const offlineCurrentUploadNames = ref([]);
const offlineSources = ref([]);
const offlinePopulations = ref([]);
const offlinePopulationStats = ref([]);
const offlineFeeds = ref([]);
const offlinePresets = ref([]);
export default function useOfflinePartner() {
  const flashesStore = useFlashesStore();
  const { currentOrg } = useAuth();
  const { trackDataSharingChange } = useDataSharingTracking();
  async function refreshOfflineData(offlinePartnerUuid, rethrow = false) {
    try {
      const [
        { data: currentUploadNamesData },
        { data: sourcesData },
        { data: feedsData },
        { data: populationsData, error: populationsError },
        {
          data: offlinePopulationStatsData,
          error: offlinePopulationStatsError,
        },
        { data: presetsData },
      ] = await Promise.all([
        axios.get(urls.offlinePartners.dataSources.tables(offlinePartnerUuid)),
        axios.get(urls.offlinePartners.dataSources.sources(offlinePartnerUuid)),
        axios.get(urls.offlinePartners.dataSources.feeds(offlinePartnerUuid)),
        crossbeamApi.GET('/v0.3/populations', {
          params: { query: { offline_partner_org_uuid: offlinePartnerUuid } },
        }),
        crossbeamApi.GET('/alpha/population-stats', {
          params: { query: { offline_partner_org_uuid: offlinePartnerUuid } },
        }),
        axios.get(urls.offlinePartners.dataSources.presets(offlinePartnerUuid)),
      ]);

      if (populationsError || offlinePopulationStatsError) {
        flashesStore.addErrorFlash({
          message: 'Could not refresh offline partner data',
        });
        captureException(populationsError || offlinePopulationStatsError);
        if (rethrow) throw populationsError || offlinePopulationStatsError;
        return;
      }

      offlineCurrentUploadNames.value = currentUploadNamesData.items;
      offlineSources.value = sourcesData.items;
      offlineFeeds.value = feedsData.items;
      offlinePopulations.value = populationsData.items;
      offlinePopulationStats.value = offlinePopulationStatsData.items;
      offlinePresets.value = presetsData;
    } catch (err) {
      flashesStore.addErrorFlash({
        message: 'Could not refresh offline partner data',
      });
      captureException(err);
      if (rethrow) throw err;
    }
  }

  async function deleteOfflineSource(sourceId, offlinePartnerUuid) {
    try {
      const url = urls.offlinePartners.dataSources.delete(
        sourceId,
        offlinePartnerUuid,
      );
      await axios.delete(url);
      flashesStore.addSuccessFlash({ message: 'Data deleted' });
    } catch (err) {
      flashesStore.addErrorFlash({ message: 'Could not delete data' });
      captureException(err);
    } finally {
      await refreshOfflineData(offlinePartnerUuid);
    }
  }

  async function deleteOfflinePopulation(populationId, offlinePartnerUuid) {
    const { error } = await crossbeamApi.DELETE('/v0.2/populations/{id}', {
      params: {
        path: { id: populationId },
        query: { offline_partner_org_uuid: offlinePartnerUuid },
      },
    });
    if (error) throw error;
    await refreshOfflineData(offlinePartnerUuid);
  }

  async function setOfflinePopulationDataShare(
    populationId,
    sourceId,
    offlinePartnerUuid,
  ) {
    await refreshOfflineData(offlinePartnerUuid);

    const offlineDefaultPreset = offlinePresets.value.find(
      (preset) =>
        preset.base_source_id === sourceId &&
        preset.preset_type === 'crossbeam_default',
    );

    if (!offlineDefaultPreset) {
      return;
    }

    const payload = {
      partner_org_id: currentOrg.value.id,
      offline_partner_org_uuid: offlinePartnerUuid,
      data_shares: [
        {
          population_id: populationId,
          visibility: GREENFIELD_SHARING,
          preset_id: offlineDefaultPreset.id,
        },
      ],
    };

    const { error } = await crossbeamApi.POST('/v0.1/data-share-assignments', {
      body: payload,
    });

    if (error) {
      captureException(error);
      flashesStore.addErrorFlash({
        message:
          'Could not configure sharing rules for offline partner population',
      });
      return;
    }

    const population = offlinePopulations.value.find(
      (p) => p.id === populationId,
    );
    trackDataSharingChange({
      populationId,
      visibility: GREENFIELD_SHARING,
      presetId: offlineDefaultPreset.id,
      segment: population?.segment,
      partnerId: null,
      recommendedPresetId: offlineDefaultPreset.id,
      eventSite: EVENT_SITES.SHARING_SETTINGS_MODAL,
    });
  }

  return {
    refreshOfflineData,
    offlinePopulations,
    offlineSources,
    offlineCurrentUploadNames,
    offlineFeeds,
    offlinePresets,
    deleteOfflineSource,
    offlinePopulationStats,
    deleteOfflinePopulation,
    setOfflinePopulationDataShare,
  };
}
